import { Component, computed, inject, input, output, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { DocumentUploadStatus } from '../../../../core/enums/data.enums';
import { DocumentUploadProgressModel } from '../../../../core/models/ui/document-view.model';
import { DocumentsMessagingConstants } from '../../../../core/constants/messaging.constants';
import { DocumentsConstants } from '../../../../core/constants/documents.constants';
import { DocumentService } from '../../../../core/services/document.service';
import { TooltipDirective } from '../../../../core/directives/tooltip.directive';

@Component({
    selector: 'ms-document-upload-overlay',
    standalone: true,
    imports: [CommonModule, FontAwesomeModule, TooltipDirective],
    templateUrl: './document-upload-overlay.component.html',
    styleUrl: './document-upload-overlay.component.scss'
})
export class DocumentUploadOverlayComponent {
    public isDragging = input<boolean>(false);
    public uploads = input<DocumentUploadProgressModel[]>([]);

    public closeDock = output<void>();
    public cancelUpload = output<number>();
    public retryUpload = output<number>();

    private documentService = inject(DocumentService);

    public DocumentUploadStatus = DocumentUploadStatus;
    public DocumentsMessagingConstants = DocumentsMessagingConstants;
    public isDockCollapsed = signal<boolean>(false);

    // Shown on the drop overlay so the per-file cap is visible before the user drops files.
    public readonly maxFileSizeHint = DocumentsMessagingConstants.DOCUMENTS_UPLOAD_MAX_SIZE_HINT()
        .replaceValuesWithObject({ limit: DocumentsConstants.MAX_FILE_SIZE_LABEL() });

    // Total declared size of the queued batch, surfaced in the dock header before/while uploading.
    public batchTotalLabel = computed<string>(() =>
        DocumentsMessagingConstants.DOCUMENTS_UPLOAD_BATCH_TOTAL().replaceValuesWithObject({
            size: this.documentService.formatBytes(this.uploads().reduce((sum, u) => sum + (u.sizeBytes ?? 0), 0))
        })
    );

    public summary = computed<{ done: number; total: number; failed: number; inProgress: number }>(() => {
        const u = this.uploads();
        return {
            total: u.length,
            done: u.filter(x => x.status === DocumentUploadStatus.Done).length,
            failed: u.filter(x => x.status === DocumentUploadStatus.Failed).length,
            inProgress: u.filter(x => x.status === DocumentUploadStatus.Uploading || x.status === DocumentUploadStatus.Pending).length
        };
    });

    // Position of the item currently being processed: every resolved item (done/failed) plus the
    // one in flight, capped at the total. Counting only `done` would stall this when an earlier
    // item failed.
    public currentPosition = computed<number>(() => {
        const s = this.summary();
        const resolved = s.done + s.failed;
        return Math.min(resolved + 1, s.total);
    });

    public isComplete = computed<boolean>(() => this.summary().inProgress === 0 && this.summary().total > 0);

    public toggleDock(): void {
        this.isDockCollapsed.update(v => !v);
    }

    public dismissDock(): void {
        this.closeDock.emit();
    }

    public statusLabel(status: DocumentUploadStatus): string {
        switch (status) {
            case DocumentUploadStatus.Done: return 'Done';
            case DocumentUploadStatus.Failed: return 'Failed';
            default: return 'Pending';
        }
    }

    public statusIcon(status: DocumentUploadStatus): string {
        switch (status) {
            case DocumentUploadStatus.Done: return 'circle-check';
            case DocumentUploadStatus.Failed: return 'circle-xmark';
            case DocumentUploadStatus.Uploading: return 'arrow-up-from-bracket';
            default: return 'clock';
        }
    }

    public statusClass(status: DocumentUploadStatus): string {
        switch (status) {
            case DocumentUploadStatus.Done: return 'text-success';
            case DocumentUploadStatus.Failed: return 'text-danger';
            case DocumentUploadStatus.Uploading: return 'text-primary';
            default: return 'text-muted';
        }
    }
}
